import { computed, effect, Injectable, NgZone, untracked, signal, inject } from '@angular/core';
import { isInputError } from 'src/util/functions';
import { IAccessToken } from 'src/models/auth.model';
import { Router } from '@angular/router';
import { InitSignRoute, LOGIN_REDIRECT_FIRST_SIGN_IN_CREATE_INVESTMENT_PATH, LOGIN_REDIRECT_PATH, OauthRoute } from 'src/models/routes.model';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { AuthDataService } from 'src/data-store/auth-data.service';
import { UserDataService } from 'src/data-store/user-data.service';
import { DeviceService } from './device.service';
import { LoggerService } from './logger.service';
import { DataService } from 'src/data-store/data.service';
import { RestApiService } from './rest-api.service';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';
import { LogoutService } from './logout.service';
import { OauthLoginService } from './oauth-login.service';
import { LOCAL_STORAGE_KEYS, LocalStorageService } from './local-storage.service';
import { take } from 'rxjs';
import { parseToError } from 'src/util/functions-angular';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private router = inject(Router);
  private authDataService = inject(AuthDataService);
  private userDataService = inject(UserDataService);
  private zone = inject(NgZone);
  private deviceService = inject(DeviceService);
  private loggerService = inject(LoggerService);
  private dataService = inject(DataService);
  private restApiService = inject(RestApiService);
  private logoutService = inject(LogoutService);
  private oauthLoginService = inject(OauthLoginService);
  private notificationService = inject(NotificationService);
  private translateService = inject(TranslateService);
  private localStorageService = inject(LocalStorageService);

  public constructor() {
    this.redirectFromLoginEffect();
    this.getAuthTokenDataEffect();
    this.getCurrentUserEffect();
    this.loadInvestmentsEffect();
    this.getInvestmentsEffect();
  }

  private initiated = false;
  private initiatedInvestmentsLoad = false;
  private loginRedirected = false;
  private disclaimerPosted = signal<boolean>(false);
  private initiatedInvestments = signal<boolean>(false);
  private userAndTokenExist = computed<boolean>(() => !!this.userDataService.loggedUser() && !!this.authDataService.accessToken());
  private investmentDataLoaded = computed<boolean>(() =>
    this.dataService.portfolios() &&
    !this.dataService.productsIsLoading(ELoadingEntity.PORTFOLIO_OVERVIEW_LIST) &&
    this.dataService.portfoliosTotal !== null
  );

  private disclaimerShownAt = computed<boolean>(() => !!this.userDataService.loggedUser()?.disclaimerShownAt);

  public logIn(bcasKey: string): void {
    this.oauthLoginService.logIn(bcasKey);
  }

  private getAuthTokenDataEffect() {
    effect(() => {
      const tokenData = this.authDataService.tokenData();
      const authError = this.authDataService.authGetError(ELoadingEntity.AUTH_TOKEN_DATA);
      if (tokenData) {
        this.validateTokenData(tokenData);
      }
      if (authError?.error) {
        this.backendError(authError.error);
        untracked(() => this.authDataService.authClearError(ELoadingEntity.AUTH_TOKEN_DATA));
      }
    });
  }

  private validateTokenData(data: unknown): void {
    if (!this.validateApiResponse(data)) {
      this.loggerService.error('Aceess token data response error.', data);
      this.backendError();
    }
  }

  private redirectFromLoginEffect(): void {
    effect(() => {
      // to enable manual url change on web without redirect to dashboard
      if (
        !this.deviceService.isNativePlatform &&
        !window.location.pathname.includes('/' + OauthRoute.BCAS_CALLBACK)
      ) return;

      const disclaimerShownAt = untracked(this.disclaimerShownAt);
      const alreadyWithInvestment = untracked(this.investmentDataLoaded);
      const investments = untracked(this.dataService.portfolios);
      const userAndTokenExist = this.userAndTokenExist();

      if(!userAndTokenExist) {
        this.loginRedirected = false;
        return;
      }

      if(this.loginRedirected) return;

      const logRedirectRoute: (string|number)[] = [];
      if(disclaimerShownAt) {
        logRedirectRoute.push(LOGIN_REDIRECT_PATH);
      } else {
        if (alreadyWithInvestment && investments.length) {
          logRedirectRoute.push('/' + InitSignRoute.INIT_SIGN_WITH_INVESTMENTS);
          logRedirectRoute.push(investments[0].portfolioId + '');
        } else {
          logRedirectRoute.push(LOGIN_REDIRECT_FIRST_SIGN_IN_CREATE_INVESTMENT_PATH);
        }
      }

      if (userAndTokenExist && (disclaimerShownAt || (this.investmentDataLoaded() && this.initiatedInvestments()))) {
        this.loginRedirected = true;
        this.localStorageService
          .getItem({ key: LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN })
          .pipe(take(1)).subscribe(afterAuthRedirectRoute => {
            // todo - routes priority https://gitlab.com/dfcz/bwm-frontend/-/work_items/164
            if(afterAuthRedirectRoute) {
              this.redirectTo([afterAuthRedirectRoute], disclaimerShownAt);
              this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN });
            } else {
              this.redirectTo(logRedirectRoute, disclaimerShownAt);
            }

            if (!disclaimerShownAt) {
              untracked(() => this.postDisclaimerShown());
            }
          }
        );
      }
    });
  }

  // must run only after the first-sign-in redirect, otherwise the refreshed user would skip it
  private postDisclaimerShown(): void {
    if (this.disclaimerPosted()) return;

    this.disclaimerPosted.set(true);
    this.restApiService.setDisclaimerShown().subscribe({
      next: () => this.userDataService.getCurrentUser(),
      error: (error) => this.loggerService.error(error),
    });
  }

  private redirectTo(logRedirectRoute: (string | number)[], disclaimerShownAt: boolean) {
    if (this.deviceService.isNativePlatform) {
      this.zone.run(() => {
        this.router.navigate(logRedirectRoute, { queryParams: disclaimerShownAt ? undefined : { userMode: 'new' } });
      });
    } else {
      this.router.navigate(logRedirectRoute, { queryParams: disclaimerShownAt ? undefined : { userMode: 'new' } });
    }
  }

  private getCurrentUserEffect(): void {
    effect(() => {
      const { loggedUser, contractsStats } = this.userDataService;
      const { logoutIdToken, accessToken } = this.authDataService;
      if ((!this.initiated && accessToken()) || ((!loggedUser() || !contractsStats()) && !logoutIdToken() && accessToken())) {
        this.userDataService.getCurrentUser();
        if (!this.initiated) this.initiated = true;
      }
    });
  }

  private loadInvestmentsEffect(): void {
    effect(() => {
      const loggedUser = this.userDataService.loggedUser();
      const disclaimerShownAt = untracked(this.disclaimerShownAt);

      if (this.initiated && loggedUser && !this.initiatedInvestmentsLoad && !disclaimerShownAt) {
        this.dataService.getPortfolioOverviews({});
        if (!this.initiatedInvestmentsLoad) this.initiatedInvestmentsLoad = true;
      }
    });
  }

  private getInvestmentsEffect(): void {
    effect(() => {
      const portfolios = this.dataService.portfolios();
      if (this.initiatedInvestmentsLoad && portfolios && this.initiated) {
        if (!this.initiatedInvestments()) {
          this.initiatedInvestments.set(true);
        }
      }
    });
  }

  private validateApiResponse(data: unknown): data is IAccessToken {
    if (typeof data === 'object' &&
      data !== null &&
      'access_token' in data && typeof data.access_token === 'string' &&
      'expires_in' in data && typeof data.expires_in === 'number' &&
      'refresh_token' in data && typeof data.refresh_token === 'string' &&
      'token_type' in data && typeof data.token_type === 'string'
    ) {
        return true;
    }
    this.notificationService.error(
      this.translateService.instant('toastNotifications.headers.authError'),
      this.translateService.instant('toastNotifications.messages.serverAuthError'),
    );
    return false;
  }

  private backendError(error?: unknown): void {
    const apiErrorCodes = [
      'E001', // auth successfull but without rights
      'E016', // not auth by BCAS
      'E017', // not in db/wrong gained oauth data
    ];
    const apiStatusCodes = [
      401,
      422,
    ];

    const parsedError = parseToError(error);

    if(parsedError &&
      apiStatusCodes.includes(parsedError.status) &&
      apiErrorCodes.includes(parsedError.error.errorCode)
    ) {
      if(parsedError.error.idToken) {
        this.logoutService.finishLogout({
          idToken: parsedError.error.idToken,
          displayErrorOnRedirectBack: 'client',
        });
      } else {
        this.logoutService.finishLogout({displayErrorOnRedirectBack: 'client'});
      }
    } else {
      this.notificationService.error(
        this.translateService.instant('toastNotifications.headers.authError'),
        this.translateService.instant('toastNotifications.messages.authError'),
      );
      this.router.navigate(['/login']);
    }

    if(isInputError(error, 401)) {
      this.logoutService.finishLogout({displayErrorOnRedirectBack: 'client'});
    }

  };
}
